import {Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MatIconRegistry} from "@angular/material/icon";
import {DomSanitizer} from "@angular/platform-browser";
import {ConsoleUserService} from '@core-services/console-user.service';
import {TabManagementService} from "@core-services/tab-management.service";
import {PageEventInterface} from "@core/interfaces/page-event.interface";
import {PageOffering} from '@core/model/side-nav.model';
import {LeaseBookingsComponent} from "@features/leasing/lease-bookings/lease-bookings.component";
import {SelectableCategory} from "@features/settings/industries/industry.model";
import {ManageIndustriesService} from "@features/settings/industries/manage-industries.service";
import {ConsoleContextService} from "@security/console-context.service";
import {UtilsService} from "@shared-services/utils.service";
import {UpdateUserContactRequest, User} from '@ui-kit/framework/security/model/user.model';
import {LoadingService} from '@ui-kit/framework/util/service/loading.service';
import {CustomValidators} from '@ui-kit/framework/util/validators/custom-validators';
import {Retailer} from "../../retailers.model";
import {RetailerViewComponent} from "../retailer-view.component";
import {RetailerViewService} from "../retailer-view.service";

@Component({
  selector: 'app-retailer-details',
  templateUrl: './retailer-details.component.html',
  styleUrls: ['./retailer-details.component.scss']
})
export class RetailerDetailsComponent implements OnInit {
  @Input() id!: string;
  @Input() header?: string = 'Retailer Details'
  @Input() createLease?: boolean = false;
  @Input() offering!: PageOffering;
  @Input() allowDownload: boolean = false;

  @Output() downloadApplication = new EventEmitter<void>();

  user!: User;
  editingOwnerInformation: boolean = false;

  retailer!: Retailer;
  editingCompanyInformation: boolean = false;
  userContactForm: FormGroup;
  companyInformationForm: FormGroup;
  otpOptions = [{
    value: 'MOBILE_PHONE',
    label: 'SMS'
  },
    {
      value: 'EMAIL',
      label: 'Email'
    }]
  private _loader = inject(LoadingService);
  protected loading$ = this._loader.loading$;

  industriesList: SelectableCategory[] = [];

  constructor(public context: ConsoleContextService,
              private retailerService: RetailerViewService,
              private tabService: TabManagementService,
              public utils: UtilsService,
              private consoleUserService: ConsoleUserService,
              private industriesService: ManageIndustriesService,
              private matIconRegistry: MatIconRegistry,
              private domSanitizer: DomSanitizer) {
    this.matIconRegistry.addSvgIcon('file_download', this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/form-icons/file_download.svg'));

    this.userContactForm = new FormGroup({
      phoneNumber: new FormControl(null, Validators.pattern('^((\\+91-?)|0)?[0-9]{10}$')),
      otpPreference: new FormControl(null, Validators.required),
    });

    this.companyInformationForm = new FormGroup({
      websiteUrl: new FormControl(null, CustomValidators.websiteUrl.bind(this)),
      socialLink: new FormControl(null, CustomValidators.websiteUrl.bind(this)),
      phoneNumber: new FormControl(null, Validators.pattern('^((\\+91-?)|0)?[0-9]{10}$')),
      externalTenantCode: new FormControl(null)
    })
  }

  ngOnInit() {
    this.loadRetailer();
    this.disableCompanyInformationForm();
    this.disableOwnerInformationForm();
    this.loadIndustries();
  }

  loadRetailer() {
    this.retailerService.getRetailerDetails(this.id).subscribe({
      next: value => {
        this.retailer = value;
        this.setCompanyInformationForm();
        this.loadRetailerOwner();
      },
      error: err => {
        console.log(err);
      }
    });
  }

  loadRetailerOwner() {
    this.consoleUserService.getUser(this.retailer.ownerId).subscribe({
      next: value => {
        this.user = value;
        this.setOwnerInformationForm();
      },
      error: err => {
        console.log(err);
      }
    });
  }

  loadIndustries() {
    this.industriesService.getAllSelectableOptionsForOffering().subscribe({
      next: value => {
        this.industriesList = value;
      },
      error: err => {
        console.log(err);
      }
    })
  }

  displayIndustriesList(retailer: Retailer): string {
    const allIndustries = this.industriesList.flatMap(fm => fm.industries);
    const industryArray = retailer.industries
      .map(m => allIndustries.find(f => f.id == m.industryTypeId))
      .filter(f => !!f)
      .map(m => m!.labelNameOverride ?? m!.name);
    return this.utils.displayListAsString(industryArray);
  }

  openCreateLeaseTab() {
    let payload: PageEventInterface = {
      componentToRegister: LeaseBookingsComponent,
      pageName: 'Create a lease',
      pageHeader: this.offering.label,
      data: { retailerId: this.id },
      id: this.utils.generateUuidWithPrefix('lease-bookings'),
      offeringId: this.offering.offeringUuid,
      sectionCode: 'leasing',
      pageCode: 'lease-bookings',
      offering: this.offering
    }
    this.tabService.sendPageAddEvent(payload);
  }

  openRetailerViewTab() {
    let payload: PageEventInterface = {
      componentToRegister: RetailerViewComponent,
      pageName: 'Retailer ' + this.retailer.id,
      pageHeader: this.offering.label,
      data: { id: this.retailer.id, companyName: this.retailer.companyName, tenantType: this.retailer.tenantType },
      id: this.utils.generateUuidWithPrefix('retailer'),
      offeringId: this.offering.offeringUuid,
      sectionCode: 'leads',
      pageCode: 'retailer',
      offering: this.offering
    }
    this.tabService.sendPageAddEvent(payload);
  }

  saveRetailerInformation(): void {
    let request: any = {
      externalTenantCode: this.companyInformationForm.get('externalTenantCode')?.value,
      websiteUrl: this.companyInformationForm.get('websiteUrl')?.value,
      socialMediaUrl: this.companyInformationForm.get('socialLink')?.value,
      mobile: this.companyInformationForm.get('phoneNumber')?.value,
    }

    this.retailerService.updateRetailer(this.retailer!.id!.toString(), request)
      .subscribe({
        next: value => {
          this.retailer = value;
          this.setCompanyInformationForm();
          this.editCompanyInformation(false);
        }, error: err => {
          console.error(err);
        }
      })

  }

  editOwnerInformation(isEditing: boolean): void {
    this.editingOwnerInformation = isEditing;
    if (isEditing) {
      this.enableOwnerInformationForm();
    } else {
      this.disableOwnerInformationForm();
    }
  }

  editCompanyInformation(isEditing: boolean): void {
    this.editingCompanyInformation = isEditing;
    if (isEditing) {
      this.enableCompanyInformationForm();
    } else {
      this.disableCompanyInformationForm();
    }
  }

  updateOwnerInformation(): void {
    //   update contact info and preferences
    if (this.userContactForm.valid) {
      const request: UpdateUserContactRequest = {
        userId: this.user.id!,
        userEmail: this.user ? this.user.email : '',
        mobileNumber: this.userContactForm.get('phoneNumber')?.value,
        otpContactPreference: this.userContactForm.get('otpPreference')?.value,
      }

      this.consoleUserService.updateUserContactAndOtpPreferences(request)
        .subscribe({
          next: value => {
            this.user = value;
            this.setOwnerInformationForm();
            this.editOwnerInformation(false);
          },
          error: err => {
            console.error(err);
          }
        });
    }

  }

  private setOwnerInformationForm(): void {
    let primaryMobile: any = this.user.contacts?.filter(m => m.contactType.code == 'MOBILE_PHONE')[0]?.value;
    this.userContactForm.get('phoneNumber')?.setValue(primaryMobile);
    this.userContactForm.get('otpPreference')?.setValue(this.user.otpContactPreference);
  }

  private setCompanyInformationForm(): void {
    this.companyInformationForm.get('websiteUrl')?.setValue(this.retailer.websiteUrl);
    this.companyInformationForm.get('socialLink')?.setValue(this.retailer.socialLink);
    this.companyInformationForm.get('phoneNumber')?.setValue(this.retailer.phoneNumber);
    this.companyInformationForm.get('externalTenantCode')?.setValue(this.retailer.externalTenantCode);
  }

  private disableCompanyInformationForm(): void {
    this.companyInformationForm.get('websiteUrl')?.disable();
    this.companyInformationForm.get('socialLink')?.disable();
    this.companyInformationForm.get('phoneNumber')?.disable();
    this.companyInformationForm.get('externalTenantCode')?.disable();
  }

  private enableCompanyInformationForm(): void {
    this.companyInformationForm.get('websiteUrl')?.enable();
    this.companyInformationForm.get('socialLink')?.enable();
    this.companyInformationForm.get('phoneNumber')?.enable();
    this.companyInformationForm.get('externalTenantCode')?.enable();
  }

  private disableOwnerInformationForm(): void {
    this.userContactForm.get('phoneNumber')?.disable();
    this.userContactForm.get('otpPreference')?.disable();
  }

  private enableOwnerInformationForm(): void {
    this.userContactForm.get('phoneNumber')?.enable();
    this.userContactForm.get('otpPreference')?.enable();
  }
}
